import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

/* ═══════════════════════════════════════════════
   HOOK RESPONSIVE
═══════════════════════════════════════════════ */
const useWindowSize = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  return width;
};

/* ═══════════════════════════════════════════════
   MOBILE TOP BAR
═══════════════════════════════════════════════ */
const MobileTopBar = ({ stats }) => (
  <div style={mb.topBar}>
    <div style={mb.logo}>Trans<span style={{ color: "#16a34a" }}>TiGo</span></div>
    {stats && (
      <div style={mb.stats}>
        {stats.map(([v, l]) => (
          <div key={l} style={mb.statItem}>
            <div style={mb.statVal}>{v}</div>
            <div style={mb.statLabel}>{l}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════
   ICONS
═══════════════════════════════════════════════ */
const UserIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="#9ca3af" strokeWidth="1.5"/><path d="M2 13c0-2.8 2.7-5 6-5s6 2.2 6 5" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const MailIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="10" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M1 6l7 4 7-4" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const PhoneIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 1h4l1.5 4-2 1.2a9.1 9.1 0 004.3 4.3L12 8.5 16 10v4a2 2 0 01-2 2A15 15 0 011 3a2 2 0 012-2z" stroke="#9ca3af" strokeWidth="1.5" strokeLinejoin="round"/></svg>;
const LockIcon   = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="3" y="7" width="10" height="8" rx="2" stroke="#9ca3af" strokeWidth="1.5"/><path d="M5 7V5a3 3 0 016 0v2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeIcon    = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#9ca3af" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="#9ca3af" strokeWidth="1.5"/></svg>;
const EyeOffIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M6.5 6.6A2 2 0 0010 10M4 4.3C2.4 5.4 1 8 1 8s2.5 5 7 5c1.4 0 2.7-.4 3.8-1M7 3.1C7.3 3 7.6 3 8 3c4.5 0 7 5 7 5s-.6 1.2-1.6 2.3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const AlertIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink:0 }}><circle cx="8" cy="8" r="7" stroke="#f87171" strokeWidth="1.5"/><path d="M8 5v4M8 11v.5" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const CheckIcon  = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink:0 }}><circle cx="8" cy="8" r="7" stroke="#4ade80" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const SpinIcon   = () => <span style={{ display:"inline-block", width:14, height:14, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"white", borderRadius:"50%", animation:"spin 0.6s linear infinite", marginRight:8, verticalAlign:"middle" }} />;

/* ═══════════════════════════════════════════════
   FIELD
═══════════════════════════════════════════════ */
const Field = ({ label, icon, suffix, error, hint, style: fieldStyle, ...inputProps }) => (
  <div style={fieldStyle}>
    {label && <label style={s.label}>{label}</label>}
    <div style={{ position: "relative" }}>
      {icon && <span style={s.inputIcon}>{icon}</span>}
      <input style={{ ...s.input, paddingLeft: icon ? 42 : 14, paddingRight: suffix ? 42 : 14, borderColor: error ? "#f87171" : undefined }} {...inputProps} />
      {suffix && <button style={s.eyeBtn} type="button" onClick={suffix.onClick}>{suffix.node}</button>}
    </div>
    {error && <div style={s.fieldError}>{error}</div>}
    {hint && !error && <div style={s.hint}>{hint}</div>}
  </div>
);

/* ═══════════════════════════════════════════════
   PASSWORD STRENGTH
═══════════════════════════════════════════════ */
const pwStrength = (p) => {
  if (!p) return { score: 0, label: "", color: "#e0e0e0" };
  let score = 0;
  if (p.length >= 8)           score++;
  if (/[A-Z]/.test(p))         score++;
  if (/[0-9]/.test(p))         score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const map = [
    { label: "",          color: "#e0e0e0" },
    { label: "Faible",    color: "#f87171" },
    { label: "Moyen",     color: "#fbbf24" },
    { label: "Bon",       color: "#34d399" },
    { label: "Excellent", color: "#4ade80" },
  ];
  return { score, ...map[score] };
};

/* ═══════════════════════════════════════════════
   PANEL GAUCHE (desktop)
═══════════════════════════════════════════════ */
const PanelLeft = () => (
  <div style={s.panel}>
    <svg style={s.panelGrid} viewBox="0 0 400 700" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="g2" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="400" height="700" fill="url(#g2)"/>
      <circle cx="50"  cy="200" r="200" fill="rgba(74,222,128,0.04)"/>
      <circle cx="350" cy="550" r="160" fill="rgba(74,222,128,0.05)"/>
      <circle cx="200" cy="350" r="70"  fill="none" stroke="rgba(74,222,128,0.1)" strokeWidth="0.5"/>
      <circle cx="200" cy="350" r="130" fill="none" stroke="rgba(74,222,128,0.06)" strokeWidth="0.5"/>
    </svg>
    <div style={s.panelContent}>
      <div style={s.panelLogo}>Trans<span style={{ color: "#4ade80" }}>TiGo</span></div>
      <div style={{ margin: "auto 0" }}>
        <h2 style={s.panelTitle}>Rejoignez la communauté</h2>
        <p style={s.panelSub}>Accédez aux meilleurs transporteurs vérifiés de Madagascar, partout et à tout moment.</p>
      </div>
      <ul style={s.checklist}>
        {["Réservation en ligne en 2 minutes", "Billet numérique sur votre téléphone", "Transporteurs vérifiés et notés", "Support client 7j/7"].map(item => (
          <li key={item} style={s.checkItem}>
            <span style={{ color: "#4ade80", flexShrink: 0 }}>✓</span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{item}</span>
          </li>
        ))}
      </ul>
      <div style={s.panelStats}>
        {[["2 841", "Voyageurs"], ["138", "Transporteurs"], ["4.8★", "Note moy."]].map(([v, l]) => (
          <div key={l}>
            <div style={s.statVal}>{v}</div>
            <div style={s.statLabel}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   REGISTER
═══════════════════════════════════════════════ */
const Register = () => {
  const navigate  = useNavigate();
  const { login } = useAuth();
  const w         = useWindowSize();
  const isMobile  = w <= 768;

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "" });
  const [loading,     setLoading]  = useState(false);
  const [error,       setError]    = useState("");
  const [showPwd,     setShowPwd]  = useState(false);
  const [showCPwd,    setShowCPwd] = useState(false);
  const [fieldErrors, setFE]       = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setFE(fe => ({ ...fe, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim())    errs.firstName = "Requis";
    if (!form.lastName.trim())     errs.lastName  = "Requis";
    if (!form.email.includes("@")) errs.email     = "Email invalide";
    if (form.password.length < 6)  errs.password  = "6 caractères minimum";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "Les mots de passe ne correspondent pas";
    return errs;
  };

  const handleRegister = async () => {
    setError("");
    const errs = validate();
    if (Object.keys(errs).length) { setFE(errs); return; }
    setLoading(true);
    try {
      const res = await api.post("/register", { firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone, password: form.password });
      login(res.data.token, null, res.data.user);
      navigate("/dashboard");
    } catch { setError("Erreur lors de l'inscription. Cet email est peut-être déjà utilisé."); }
    setLoading(false);
  };

  const strength = pwStrength(form.password);

  return (
    <div style={{ ...s.page, flexDirection: isMobile ? "column" : "row" }}>
      <style>{globalCSS}</style>

      {!isMobile && <PanelLeft />}

      <div style={{ ...s.formSide, padding: isMobile ? "0 20px 48px" : "40px 32px", alignItems: isMobile ? "flex-start" : "flex-start" }}>
        {isMobile && (
          <MobileTopBar stats={[["2 841", "Voyageurs"], ["138", "Transporteurs"], ["4.8★", "Note moy."]]} />
        )}

        <div style={{ ...s.formBox, maxWidth: 460, paddingTop: isMobile ? 0 : 20 }}>
          <div style={{ ...s.formHeader, textAlign: isMobile ? "center" : "left" }}>
            <h1 style={{ ...s.formTitle, fontSize: isMobile ? 22 : 26 }}>Créer un compte</h1>
            <p style={s.formSub}>Déjà inscrit ? <Link to="/login" style={s.link}>Se connecter</Link></p>
          </div>

          {error && <div style={s.errorBox}><AlertIcon />{error}</div>}

          {/* Prénom / Nom */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <Field label="Prénom" icon={<UserIcon />} name="firstName" placeholder="Votre prénom" value={form.firstName} onChange={handleChange} error={fieldErrors.firstName} />
            <Field label="Nom"    icon={<UserIcon />} name="lastName"  placeholder="Votre nom"    value={form.lastName}  onChange={handleChange} error={fieldErrors.lastName} />
          </div>

          <Field label="Adresse email" icon={<MailIcon />}  name="email" type="email" placeholder="vous@exemple.mg"   value={form.email} onChange={handleChange} error={fieldErrors.email} style={{ marginBottom: 14 }} />
          <Field label="Téléphone"     icon={<PhoneIcon />} name="phone" type="tel"   placeholder="+261 34 XXX XXX"   value={form.phone} onChange={handleChange} style={{ marginBottom: 14 }} />

          {/* Mot de passe */}
          <div style={{ marginBottom: 6 }}>
            <label style={s.label}>Mot de passe</label>
            <div style={{ position: "relative" }}>
              <span style={s.inputIcon}><LockIcon /></span>
              <input style={{ ...s.input, paddingLeft: 42, paddingRight: 42, borderColor: fieldErrors.password ? "#f87171" : undefined }}
                type={showPwd ? "text" : "password"} name="password" placeholder="Minimum 6 caractères" value={form.password} onChange={handleChange} />
              <button style={s.eyeBtn} type="button" onClick={() => setShowPwd(!showPwd)}>{showPwd ? <EyeOffIcon /> : <EyeIcon />}</button>
            </div>
            {fieldErrors.password && <div style={s.fieldError}>{fieldErrors.password}</div>}
          </div>

          {form.password && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= strength.score ? strength.color : "#e8e8e4", transition: "background 0.2s" }} />
                ))}
              </div>
              <div style={{ fontSize: 11, color: strength.color }}>{strength.label}</div>
            </div>
          )}

          {/* Confirmer */}
          <div style={{ position: "relative", marginBottom: 24 }}>
            <label style={s.label}>Confirmer le mot de passe</label>
            <div style={{ position: "relative" }}>
              <span style={s.inputIcon}><LockIcon /></span>
              <input style={{ ...s.input, paddingLeft: 42, paddingRight: 42, borderColor: fieldErrors.confirmPassword ? "#f87171" : (form.confirmPassword && form.password === form.confirmPassword ? "#4ade80" : undefined) }}
                type={showCPwd ? "text" : "password"} name="confirmPassword" placeholder="Répétez votre mot de passe" value={form.confirmPassword} onChange={handleChange} />
              <button style={s.eyeBtn} type="button" onClick={() => setShowCPwd(!showCPwd)}>{showCPwd ? <EyeOffIcon /> : <EyeIcon />}</button>
            </div>
            {fieldErrors.confirmPassword && <div style={s.fieldError}>{fieldErrors.confirmPassword}</div>}
            {form.confirmPassword && form.password === form.confirmPassword && !fieldErrors.confirmPassword && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, fontSize: 11, color: "#4ade80" }}>
                <CheckIcon /> Mots de passe identiques
              </div>
            )}
          </div>

          <button style={s.submitBtn} onClick={handleRegister} disabled={loading} className="submit-btn">
            {loading ? <><SpinIcon />Inscription…</> : "Créer mon compte"}
          </button>

          <p style={{ fontSize: 11, color: "#bbb", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
            En créant un compte, vous acceptez nos{" "}
            <Link to="/conditions" style={{ color: "#16a34a" }}>Conditions d'utilisation</Link>
            {" "}et notre{" "}
            <Link to="/confidentialite" style={{ color: "#16a34a" }}>Politique de confidentialité</Link>.
          </p>

          <div style={s.divider}><span style={s.dividerText}>vous êtes transporteur ?</span></div>
          <Link to="/register-transporter" style={s.altBtn}>Créer un compte transporteur</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;

/* ── Styles partagés ── */
const s = {
  page:         { display: "flex", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif" },
  panel:        { width: "42%", background: "#111318", position: "relative", overflow: "hidden", flexShrink: 0 },
  panelGrid:    { position: "absolute", inset: 0, width: "100%", height: "100%" },
  panelContent: { position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%", padding: "40px 44px" },
  panelLogo:    { fontSize: 22, fontWeight: 700, color: "white", letterSpacing: -0.5, marginBottom: 0 },
  panelTitle:   { fontSize: 30, fontWeight: 700, color: "white", letterSpacing: -1, lineHeight: 1.2, marginBottom: 12 },
  panelSub:     { fontSize: 14, color: "rgba(255,255,255,0.4)", lineHeight: 1.7 },
  checklist:    { listStyle: "none", padding: 0, margin: "32px 0", display: "flex", flexDirection: "column", gap: 12 },
  checkItem:    { display: "flex", alignItems: "flex-start", gap: 10 },
  panelStats:   { display: "flex", gap: 28, paddingTop: 24, borderTop: "0.5px solid rgba(255,255,255,0.08)", marginTop: "auto" },
  statVal:      { fontSize: 18, fontWeight: 700, color: "white", letterSpacing: -0.5 },
  statLabel:    { fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 2 },

  formSide:  { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", background: "white", padding: "40px 32px", overflowY: "auto" },
  formBox:   { width: "100%", maxWidth: 460 },
  formHeader:{ marginBottom: 28 },
  formTitle: { fontSize: 26, fontWeight: 700, letterSpacing: -0.8, color: "#0f1117", margin: "0 0 6px" },
  formSub:   { fontSize: 13, color: "#9ca3af", margin: 0 },
  link:      { color: "#16a34a", fontWeight: 600, textDecoration: "none" },
  errorBox:  { display: "flex", alignItems: "center", gap: 8, background: "#fff5f5", border: "0.5px solid #fecaca", borderRadius: 9, padding: "10px 14px", marginBottom: 18, fontSize: 13, color: "#dc2626" },
  fieldError:{ fontSize: 11, color: "#f87171", marginTop: 4 },
  hint:      { fontSize: 11, color: "#9ca3af", marginTop: 4 },
  label:     { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, letterSpacing: 0.2 },
  input:     { width: "100%", padding: "11px 14px", border: "0.5px solid #e0e0e0", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "#0f1117", background: "white", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s" },
  inputIcon: { position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", pointerEvents: "none" },
  eyeBtn:    { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" },
  submitBtn: { width: "100%", padding: "13px 0", background: "#0f1117", color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  divider:   { position: "relative", textAlign: "center", margin: "20px 0", borderTop: "0.5px solid #e8e8e4" },
  dividerText:{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "white", padding: "0 10px", fontSize: 11, color: "#bbb", whiteSpace: "nowrap" },
  altBtn:    { display: "block", textAlign: "center", padding: "11px 0", border: "0.5px solid #e0e0e0", borderRadius: 10, fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500, background: "white" },
};

const mb = {
  topBar:    { width: "100%", background: "#111318", padding: "24px 20px 20px", marginBottom: 28, borderRadius: "0 0 18px 18px", textAlign: "center" },
  logo:      { fontSize: 22, fontWeight: 800, color: "white", letterSpacing: -0.5, marginBottom: 16 },
  stats:     { display: "flex", justifyContent: "space-around" },
  statItem:  { display: "flex", flexDirection: "column", alignItems: "center" },
  statVal:   { fontSize: 16, fontWeight: 700, color: "white", letterSpacing: -0.5 },
  statLabel: { fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 },
};

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  input:focus { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,0.08) !important; }
  .submit-btn:hover:not(:disabled) { background: #1a1a1a !important; }
  .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
`;